import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Bell, BellOff } from 'lucide-react';

export function NotificationPreferences() {
  const [preferences, setPreferences] = useState({
    stream_start: true,
    scheduled_streams: true,
    new_follower: true,
    tips_received: true,
    poll_results: true,
    system_updates: false,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setPreferences({
          stream_start: data.stream_start,
          scheduled_streams: data.scheduled_streams ?? true,
          new_follower: data.new_follower,
          tips_received: data.tips_received,
          poll_results: data.poll_results,
          system_updates: data.system_updates,
        });
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = async (key: keyof typeof preferences, value: boolean) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      setPreferences((prev) => ({ ...prev, [key]: value }));

      const { error } = await supabase
        .from('notification_preferences')
        .upsert({
          user_id: user.id,
          [key]: value,
        });

      if (error) throw error;

      toast.success('Preferences updated');
    } catch (error) {
      console.error('Error updating preference:', error);
      toast.error('Failed to update preferences');
      // Revert on error
      loadPreferences();
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-12 bg-muted rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Notification Preferences
        </CardTitle>
        <CardDescription>
          Choose what notifications you want to receive
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="stream_start">Stream Started</Label>
            <p className="text-sm text-muted-foreground">
              Get notified when creators you follow go live
            </p>
          </div>
          <Switch
            id="stream_start"
            checked={preferences.stream_start}
            onCheckedChange={(checked) => updatePreference('stream_start', checked)}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="scheduled_streams">Scheduled Streams</Label>
            <p className="text-sm text-muted-foreground">
              Get notified when creators you follow schedule streams
            </p>
          </div>
          <Switch
            id="scheduled_streams"
            checked={preferences.scheduled_streams}
            onCheckedChange={(checked) => updatePreference('scheduled_streams', checked)}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="new_follower">New Followers</Label>
            <p className="text-sm text-muted-foreground">
              Get notified when someone follows you
            </p>
          </div>
          <Switch
            id="new_follower"
            checked={preferences.new_follower}
            onCheckedChange={(checked) => updatePreference('new_follower', checked)}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="tips_received">Tips Received</Label>
            <p className="text-sm text-muted-foreground">
              Get notified when you receive tips
            </p>
          </div>
          <Switch
            id="tips_received"
            checked={preferences.tips_received}
            onCheckedChange={(checked) => updatePreference('tips_received', checked)}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="poll_results">Poll Results</Label>
            <p className="text-sm text-muted-foreground">
              Get notified about poll results in streams you participated in
            </p>
          </div>
          <Switch
            id="poll_results"
            checked={preferences.poll_results}
            onCheckedChange={(checked) => updatePreference('poll_results', checked)}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="system_updates">System Updates</Label>
            <p className="text-sm text-muted-foreground">
              Get notified about platform updates and announcements
            </p>
          </div>
          <Switch
            id="system_updates"
            checked={preferences.system_updates}
            onCheckedChange={(checked) => updatePreference('system_updates', checked)}
          />
        </div>

        <div className="pt-4 border-t">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <BellOff className="h-4 w-4" />
            <p>You can update these preferences anytime</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
